// P-LIBRARY-READER-FIX (audit finding #1) — /play/story?slug=&mastery=
//
// The earned-story shelf opens the real reader here. `slug` names the
// story; `mastery` is an OPTIONAL optimistic hint (the library knows it
// synchronously) for an instant gold-rim paint — the reader re-derives
// Special Edition from milestone events regardless, so the flag is never
// required. Mirrors play.recap's ?subject=&mastery= search shape.
import { createFileRoute } from "@tanstack/react-router";
import { StoryReader } from "@/screens/StoryReader";
import { getStory } from "@/content/stories";

function toStr(v: unknown): string | undefined {
  return typeof v === "string" && v.length > 0 ? v : undefined;
}

// URLSearchParams has no native boolean; treat "1"/"true"/true as mastery.
function toBool(v: unknown): boolean | undefined {
  if (v === undefined || v === null || v === "") return undefined;
  return v === "1" || v === 1 || v === true || v === "true";
}

// P-READER-MARK-COMPLETED: where the reader was opened from, so finishing
// (and the header back) returns the kid to the shelf they came from.
// Defaults to the library when absent/unknown.
function toFrom(v: unknown): "library" | "read" | undefined {
  return v === "library" || v === "read" ? v : undefined;
}

// MA-790 (c): the page a capture opens the reader on. Validated exactly like
// /play/session's `resume` (a non-negative integer, else ignored), and also
// capped at 999; anything else — missing, NaN, fractional, negative, too
// large, a boolean or an array — is dropped, never thrown. StoryReader ignores a page past the story's last real page.
function toPage(v: unknown): number | undefined {
  // Only a number or a plain digit string counts (a boolean or an array would
  // otherwise coerce through Number()).
  if (typeof v !== "number" && !(typeof v === "string" && /^\d{1,3}$/.test(v))) return undefined;
  const n = Number(v);
  return Number.isInteger(n) && n >= 0 && n <= 999 ? n : undefined;
}

export const Route = createFileRoute("/play/story")({
  validateSearch: (
    search: Record<string, unknown>,
  ): { slug?: string; mastery?: boolean; from?: "library" | "read"; page?: number } => ({
    slug: toStr(search.slug),
    mastery: toBool(search.mastery),
    from: toFrom(search.from),
    page: toPage(search.page),
  }),
  // MA-190 — `loader`/`validateSearch` stay in the eagerly-bundled route
  // tree under autoCodeSplitting (only `component` is split into its own
  // chunk), so kicking off the story fetch here starts it IN PARALLEL with
  // the lazy screen chunk fetch instead of serially after it mounts.
  // Deliberately not awaited: navigation must not block on the story JSON.
  // getStory's own STORY_CACHE/INFLIGHT dedup means StoryReader's later
  // useQuery reuses this same fetch rather than issuing a second one.
  loaderDeps: ({ search }) => ({ slug: toStr(search.slug) }),
  loader: ({ deps }) => {
    if (deps.slug) void getStory(deps.slug);
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { slug, mastery, from, page } = Route.useSearch();
  return <StoryReader slug={slug} mastery={mastery} from={from} initialPage={page} />;
}
